import { FormHandles } from '@unform/core';
import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { AuthLayout } from '../../../../components/AuthLayout';
import { AuthFormHeader } from '../../../../components/AuthFormHeader';
import { Button } from '../../../../components/Button';
import { Icon } from '../../../../components/Icon';
import { InputToken } from '../../../../components/InputToken';
import { colors } from '../../../../styles/tokens';
import { useToast } from '../../../../hooks/toast';
import { api } from '../../../../services/api';

import { BackLink, Form, ResendLink } from './styles';

interface formProps {
  token: string;
}

interface locationState {
  email?: string;
}

const ForgotPasswordStep2: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { addToast } = useToast();

  const email = (state as locationState)?.email;

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password', { replace: true });
    }
  }, [email, navigate]);

  const handleSubmit = useCallback(
    async (data: formProps) => {
      try {
        formRef.current?.setErrors({});

        const schema = yup.object().shape({
          token: yup.string().required('Campo obrigatório'),
        });

        await schema.validate(data, { abortEarly: false });

        const code = data.token.toUpperCase();

        await api.post(
          'users/password/verify-token',
          { email },
          {
            headers: {
              code,
            },
          },
        );

        navigate('/forgot-password/reset', {
          state: { code },
        });
      } catch (err: any) {
        if (err instanceof yup.ValidationError) {
          formRef.current?.setErrors({ token: 'Required' });

          return;
        }

        addToast({
          type: 'error',
          title: 'Token inválido.',
        });
      }
    },
    [addToast, email, navigate],
  );

  const handleResend = useCallback(async () => {
    if (!email) return;

    await api.post('users/password/forgot', { email });

    addToast({
      type: 'info',
      title: 'Token reenviado',
      description: 'Verifique no seu email e na caixa de spam.',
    });
  }, [addToast, email]);

  return (
    <AuthLayout
      eyebrow="Protocolo de segurança"
      title={
        <>
          VERIFIQUE <span>SUA IDENTIDADE</span>
        </>
      }
      subtitle="Enviamos um código de 5 dígitos para o seu email. Insira-o para continuar a recuperação da sua senha."
    >
      <AuthFormHeader
        badge="Sessão segura"
        title="Insira o código"
        subtitle="Verifique também na caixa de spam."
      />

      <Form ref={formRef} onSubmit={handleSubmit}>
        <InputToken name="token" />

        <Button name="submit" value="Validar código" />

        <ResendLink type="button" onClick={handleResend}>
          Reenviar código
        </ResendLink>
      </Form>

      <BackLink type="button" onClick={() => navigate('/sign-in')}>
        <Icon name="arrow_back" size={18} color={colors.onSurfaceVariant} />
        Voltar ao login
      </BackLink>
    </AuthLayout>
  );
};

export { ForgotPasswordStep2 };
